import React, { useEffect, useRef } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import {
  Input,
  Select as FormFieldsSelect,
  Toggle,
} from 'components/FormFields';
import useGooglePlaces from 'hooks/useGooglePlaces';

const PacContainerStyles = createGlobalStyle`
  .pac-container {
    background: #1a1a2e;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    margin-top: 4px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
    font-family: inherit;
    z-index: 10000;
  }

  .pac-item {
    padding: 8px 12px;
    color: #94a3b8;
    border-top: 1px solid rgba(255, 255, 255, 0.05);
    cursor: pointer;
    font-size: 14px;

    &:first-child {
      border-top: none;
    }

    &:hover,
    &.pac-item-selected {
      background: rgba(250, 70, 22, 0.1);
    }
  }

  .pac-item-query {
    color: #ffffff;
    font-size: 14px;
  }

  .pac-icon {
    display: none;
  }

  .pac-matched {
    color: #fa4616;
  }
`;

const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 24px;

  /* Override Input component styles for dark theme */
  label {
    color: #94a3b8 !important;
    font-size: 13px !important;
    font-weight: 500 !important;
  }

  input,
  textarea,
  select {
    background: rgba(255, 255, 255, 0.05) !important;
    border: 1px solid rgba(255, 255, 255, 0.15) !important;
    color: #ffffff !important;

    &::placeholder {
      color: #64748b !important;
    }

    &:focus {
      border-color: #fa4616 !important;
      box-shadow: 0 0 0 1px #fa4616 !important;
    }
  }

  /* Description text styling */
  p {
    color: #94a3b8 !important;
    font-size: 12px !important;
  }
`;

const GridRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  /* Ensure dark theme overrides apply to inputs in GridRow too */
  label {
    color: #94a3b8 !important;
    font-size: 13px !important;
    font-weight: 500 !important;
  }

  input,
  textarea,
  select {
    background: rgba(255, 255, 255, 0.05) !important;
    border: 1px solid rgba(255, 255, 255, 0.15) !important;
    color: #ffffff !important;

    &::placeholder {
      color: #64748b !important;
    }

    &:focus {
      border-color: #fa4616 !important;
      box-shadow: 0 0 0 1px #fa4616 !important;
    }
  }

  /* Description text styling */
  p {
    color: #94a3b8 !important;
    font-size: 12px !important;
  }
`;

const ToggleWrapper = styled.div`
  margin-bottom: 16px;
`;

const US_STATES = [
  { label: '-- Select State --', value: '' },
  { label: 'Alabama', value: 'AL' },
  { label: 'Alaska', value: 'AK' },
  { label: 'Arizona', value: 'AZ' },
  { label: 'Arkansas', value: 'AR' },
  { label: 'California', value: 'CA' },
  { label: 'Colorado', value: 'CO' },
  { label: 'Connecticut', value: 'CT' },
  { label: 'Delaware', value: 'DE' },
  { label: 'Florida', value: 'FL' },
  { label: 'Georgia', value: 'GA' },
  { label: 'Hawaii', value: 'HI' },
  { label: 'Idaho', value: 'ID' },
  { label: 'Illinois', value: 'IL' },
  { label: 'Indiana', value: 'IN' },
  { label: 'Iowa', value: 'IA' },
  { label: 'Kansas', value: 'KS' },
  { label: 'Kentucky', value: 'KY' },
  { label: 'Louisiana', value: 'LA' },
  { label: 'Maine', value: 'ME' },
  { label: 'Maryland', value: 'MD' },
  { label: 'Massachusetts', value: 'MA' },
  { label: 'Michigan', value: 'MI' },
  { label: 'Minnesota', value: 'MN' },
  { label: 'Mississippi', value: 'MS' },
  { label: 'Missouri', value: 'MO' },
  { label: 'Montana', value: 'MT' },
  { label: 'Nebraska', value: 'NE' },
  { label: 'Nevada', value: 'NV' },
  { label: 'New Hampshire', value: 'NH' },
  { label: 'New Jersey', value: 'NJ' },
  { label: 'New Mexico', value: 'NM' },
  { label: 'New York', value: 'NY' },
  { label: 'North Carolina', value: 'NC' },
  { label: 'North Dakota', value: 'ND' },
  { label: 'Ohio', value: 'OH' },
  { label: 'Oklahoma', value: 'OK' },
  { label: 'Oregon', value: 'OR' },
  { label: 'Pennsylvania', value: 'PA' },
  { label: 'Rhode Island', value: 'RI' },
  { label: 'South Carolina', value: 'SC' },
  { label: 'South Dakota', value: 'SD' },
  { label: 'Tennessee', value: 'TN' },
  { label: 'Texas', value: 'TX' },
  { label: 'Utah', value: 'UT' },
  { label: 'Vermont', value: 'VT' },
  { label: 'Virginia', value: 'VA' },
  { label: 'Washington', value: 'WA' },
  { label: 'West Virginia', value: 'WV' },
  { label: 'Wisconsin', value: 'WI' },
  { label: 'Wyoming', value: 'WY' },
];

const COUNTRIES = [
  { label: 'United States', value: 'US' },
  { label: 'Canada', value: 'CA' },
];

const AddressForm = ({
  address,
  onChange,
  onAddressSelect,
  showSameAsCheckbox = false,
  sameAsChecked = false,
  onSameAsToggle,
  errors = {},
  idPrefix = '',
}) => {
  const { initAutocomplete } = useGooglePlaces();
  const streetInputRef = useRef(null);
  const onAddressSelectRef = useRef(onAddressSelect);
  const onChangeRef = useRef(onChange);

  // Keep refs current to avoid stale closures in the Google Places callback
  useEffect(() => {
    onAddressSelectRef.current = onAddressSelect;
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    if (!sameAsChecked && streetInputRef.current) {
      const inputEl =
        streetInputRef.current.querySelector('input') || streetInputRef.current;

      initAutocomplete(inputEl, (parsed) => {
        if (onAddressSelectRef.current) {
          onAddressSelectRef.current(parsed);
        } else {
          onChangeRef.current('street1', parsed.street1);
          onChangeRef.current('city', parsed.city);
          onChangeRef.current('state', parsed.state);
          onChangeRef.current('zipcode', parsed.zipcode);
          onChangeRef.current('country', parsed.country);
        }
      });
    }
  }, [sameAsChecked, initAutocomplete]);

  return (
    <>
      <PacContainerStyles />
      {showSameAsCheckbox && (
        <ToggleWrapper>
          <Toggle
            checked={sameAsChecked}
            onChange={(e) => onSameAsToggle(e.target.checked)}
            label="Same as previous address"
          />
        </ToggleWrapper>
      )}

      {!sameAsChecked && (
        <FormGrid>
          <div ref={streetInputRef}>
            <Input
              id={`${idPrefix}street1`}
              label="Street Address"
              placeholder="123 Main Street"
              value={address.street1 || ''}
              onChange={(e) => onChange('street1', e.target.value)}
              error={errors.street1}
              required
              autoComplete="nofill"
            />
          </div>

          <Input
            id={`${idPrefix}street2`}
            label="Apt / Suite (Optional)"
            placeholder="Suite 100"
            value={address.street2 || ''}
            onChange={(e) => onChange('street2', e.target.value)}
            autoComplete="nofill"
          />

          <GridRow>
            <Input
              id={`${idPrefix}city`}
              label="City"
              placeholder="Los Angeles"
              value={address.city || ''}
              onChange={(e) => onChange('city', e.target.value)}
              error={errors.city}
              required
              autoComplete="nofill"
            />

            <FormFieldsSelect
              label="State"
              placeholder="-- Select state --"
              value={address.state || ''}
              onChange={(e) => onChange('state', e.target.value)}
              options={US_STATES}
              error={errors.state}
              width="100%"
              required
            />
          </GridRow>

          <GridRow>
            <Input
              id={`${idPrefix}zipcode`}
              label="Zip Code"
              placeholder="90210"
              value={address.zipcode || ''}
              onChange={(e) => onChange('zipcode', e.target.value)}
              error={errors.zipcode}
              required
              autoComplete="nofill"
            />

            <FormFieldsSelect
              label="Country"
              placeholder="-- Select country --"
              value={address.country || 'US'}
              onChange={(e) => onChange('country', e.target.value)}
              options={COUNTRIES}
              width="100%"
              required
            />
          </GridRow>
        </FormGrid>
      )}
    </>
  );
};

export default AddressForm;
